import React, { useEffect, useState } from "react";

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const CalendarPopup = ({ onApply, onClose, value, variant = "range" }) => {
  const isSingle = variant === "trial";
  const isOrder = variant === "order"; // ✅ new variant
  const isRange = variant === "range";

  const today = new Date();
  today.setHours(0, 0, 0, 0); // ✅ remove time
  const minTrialDate = new Date(today);
  minTrialDate.setMonth(today.getMonth() - 2);
  minTrialDate.setHours(0, 0, 0, 0);

  const [current, setCurrent] = useState(today);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(null);
  const [showMonth, setShowMonth] = useState(false);
  const [showYear, setShowYear] = useState(false);

  const year = current.getFullYear();
  const month = current.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startWeekDay = (firstDay.getDay() + 6) % 7;

  useEffect(() => {
    if (value?.start_date) {
      const s = new Date(value.start_date);
      setStartDate(s);
      setCurrent(s);
    }
    if (value?.end_date) {
      setEndDate(new Date(value.end_date));
    }
  }, [value]);

  const selectDate = (date) => {
    // TRIAL → allow only last 2 months till today
    // if (isSingle) {
    //   if (date < minTrialDate || date > today) return;
    // }

    // ORDER → allow only future dates (your old logic)
    if (isOrder) {
      if (date < today) return;
    }

    if (isSingle || isOrder) {
      setStartDate(date);
      setEndDate(null);
      return;
    }

    // ✅ range logic (no restriction)
    if (!startDate || endDate) {
      setStartDate(date);
      setEndDate(null);
    } else if (date < startDate) {
      setEndDate(startDate);
      setStartDate(date);
    } else {
      setEndDate(date);
    }
  };

  const isInRange = (date) =>
    !isSingle && startDate && endDate && date >= startDate && date <= endDate;

  const format = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleApply = () => {
    if (!startDate) return;

    if (isSingle || isOrder) {
      onApply(format(startDate));
    } else {
      onApply({
        start_date: format(startDate),
        end_date: endDate ? format(endDate) : format(startDate),
      });
    }

    onClose?.();
  };

  return (
    <div className="w-[95vw] max-w-sm sm:w-80 bg-white rounded-2xl p-4 shadow-xl relative">
      {/* HEADER */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setCurrent(new Date(year, month - 1))}
          className="px-2 py-1 rounded bg-gray-100"
        >
          ‹
        </button>

        <div className="flex gap-2 font-semibold text-sm cursor-pointer">
          <span
            onClick={() => {
              setShowMonth(!showMonth);
              setShowYear(false);
            }}
          >
            {months[month]}
          </span>
          <span
            onClick={() => {
              setShowYear(!showYear);
              setShowMonth(false);
            }}
          >
            {year}
          </span>
        </div>

        <button
          type="button"
          onClick={() => setCurrent(new Date(year, month + 1))}
          className="px-2 py-1 rounded bg-gray-100"
        >
          ›
        </button>
      </div>

      {/* MONTH PICKER */}
      {showMonth && (
        <div className="absolute inset-x-4 top-14 bg-white rounded-xl shadow-lg p-3 grid grid-cols-3 gap-2 z-10">
          {months.map((m, i) => (
            <div
              key={m}
              onClick={() => {
                setCurrent(new Date(year, i));
                setShowMonth(false);
              }}
              className="text-sm text-center py-2 rounded cursor-pointer hover:bg-orange-100"
            >
              {m}
            </div>
          ))}
        </div>
      )}

      {/* YEAR PICKER */}
      {showYear && (
        <div className="absolute inset-x-4 top-14 bg-white rounded-xl shadow-lg p-3 grid grid-cols-3 gap-2 z-10">
          {Array.from({ length: 13 }).map((_, i) => {
            const y = year - 6 + i;
            return (
              <div
                key={y}
                onClick={() => {
                  setCurrent(new Date(y, month));
                  setShowYear(false);
                }}
                className="text-sm text-center py-2 rounded cursor-pointer hover:bg-orange-100"
              >
                {y}
              </div>
            );
          })}
        </div>
      )}

      {/* WEEK */}
      <div className="grid grid-cols-7 text-xs text-gray-400 mt-4 mb-2 text-center">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>

      {/* DAYS */}
      <div className="grid grid-cols-7">
        {Array.from({ length: startWeekDay }).map((_, i) => (
          <div key={i} />
        ))}

        {Array.from({ length: lastDay.getDate() }).map((_, i) => {
          const date = new Date(year, month, i + 1);
          date.setHours(0, 0, 0, 0); // ✅ normalize

          const isStart = startDate?.getTime() === date.getTime();
          const isEnd = endDate?.getTime() === date.getTime();

          let isDisabled = false;

          if (isSingle) {
            isDisabled = date < minTrialDate;
          }

          if (isOrder) {
            isDisabled = date < today;
          }

          return (
            <div
              key={i}
              onClick={() => selectDate(date)}
              className={`
                h-9 flex items-center justify-center cursor-pointer
                ${isInRange(date) ? "bg-orange-100" : ""}
                ${
                  isStart || isEnd
                    ? "bg-orange-500 text-white font-semibold rounded-lg"
                    : ""
                }
               ${isDisabled ? "text-gray-300 cursor-not-allowed" : "hover:bg-orange-200"}

              `}
            >
              {i + 1}
            </div>
          );
        })}
      </div>

      {/* ACTIONS */}
      <div className="flex justify-between mt-4">
        <button
          type="button"
          onClick={onClose}
          className="border border-orange-500 text-orange-500 px-4 py-1 rounded-full text-sm"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleApply}
          className="bg-orange-500 text-white px-5 py-1 rounded-full text-sm"
        >
          Apply
        </button>
      </div>
    </div>
  );
};

export default CalendarPopup;
